import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Row, Container, Spinner } from "reactstrap";
import { emptyCartAction, updateTableData } from "store/actions/cartAction";
import PricingSection from "./WaiterMenu/Cart/PricingSection";
import { addOrderAction } from "store/actions/orderActions";
import emptyCartImage from "assets/img/emptyCart.png";
import TableSelection from "./WaiterMenu/TableSelection";
import { BsCartXFill } from "react-icons/bs";
import { toast } from "react-toastify";
import "assets/scss/cart.scss";
import "assets/scss/custom.scss";
import OrderedItems from "./WaiterMenu/Cart/OrderedItems";
import { useHolidayGuard } from "hooks/useHolidayGuard";
import CustomerData from "./WaiterMenu/AddOrder/CustomerData";
import { addCustomer } from "store/actions/customerAction";

const WaiterCart = ({ toggleCart }) => {
  const dispatch = useDispatch();

  const { cartItems } = useSelector((state) => state.cart);
  const { allTablesData } = useSelector((state) => state.table);
  const { user } = useSelector((state) => state.auth);
  const { restaurantsDetail } = useSelector((state) => state.restaurant);

  const [tableNumber, setTableNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const bgColor = restaurantsDetail?.adminColor;

  const [isValid, setIsValid] = useState(false);
  const [customerAddError, setCustomerAddError] = useState("");
  const [customerData, setCustomerData] = useState({
    name: "",
    phoneNo: "",
    gender: "",
    addToExisting: false,
  });
  const [fieldErrors, setFieldErros] = useState({
    name: "",
    phone: "",
    gender: "",
  });

  const [cartData, setCartData] = useState({
    basePrice: 0,
    finalPrice: 0,
    appliedCharges: {},
  });

  useEffect(() => {
    if (customerAddError !== "") {
      setCustomerAddError("");
    }
  }, [customerData.phoneNo, customerData.addToExisting]);

  useEffect(() => {
    if (!restaurantsDetail || cartItems.length === 0) return;

    const totalAmount = cartItems?.reduce((acc, item) => {
      const price = parseFloat(item.totalPrice) || 0;
      return acc + price;
    }, 0);

    //Base price value rounded to 2 digits
    const roundedTotal = parseFloat(totalAmount.toFixed(2));

    //Now calculating final price from applied charges
    let basePrice = parseFloat(totalAmount.toFixed(2));
    let final = basePrice;
    let charges = [];

    restaurantsDetail?.additionalCharges?.forEach((charge) => {
      if (charge.isActive) {
        const fee = Number(charge.fee) || 0;
        let addedAmount = charge.type === "fixed" ? fee : (final * fee) / 100;

        final += addedAmount;
        charges.push({
          name: charge.name || "Additional Charge",
          type: charge.type,
          fee,
          addedAmount: addedAmount.toFixed(2),
        });
      }
    });
    setCartData(() => ({
      basePrice: roundedTotal,
      finalPrice: final,
      appliedCharges: charges,
    }));
  }, [cartItems, restaurantsDetail]);

  const tablesDropdown = useCallback(() => {
    const currentTime = new Date().getTime();

    return Object.keys(allTablesData?.allTables || {})
      .map((key) => {
        const table = allTablesData?.allTables[key];
        const isExpired =
          table.expireAt && currentTime > table.expireAt.toMillis();
        const isOccupied = !!table.occupiedAt;

        // Hide only if table is occupied AND not expired
        if (isOccupied && !isExpired) return null;

        return {
          ...table,
          isExpired,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.tableNo - b.tableNo);
  }, [allTablesData]);

  const { checkAndToast } = useHolidayGuard(restaurantsDetail, "dinein");

  const handlePay = (e) => {
    e.preventDefault();

    if (checkAndToast()) return;

    confirmOrder();
  };

  const confirmOrder = async () => {
    setLoading(true);
    const transformedOrder = cartItems.map(({ cartItemId, ...item }) => ({
      ...item,
      price: Number(item.price) || 0,
      totalPrice: Number(item.totalPrice) || 0,
    }));

    const { success, customerID } = await dispatch(addCustomer(customerData));

    if (!success) {
      setCustomerAddError("Error while processing customer Information");
      return;
    }

    const appliedCharges = [...cartData.appliedCharges];
    const orderPayload = {
      appliedCharges,

      currency: cartItems?.[0]?.currency || "$",
      customerID: customerID || "",
      customerName: customerData?.name || "",
      waiterID: user?.id,

      order: transformedOrder,

      orderType: "dinein",

      paymentMethod: "",
      paymentRecieved: false,
      restaurantID: cartItems?.[0]?.restaurantID || "nil",
      status: "pending",
      tableNumber: tableNumber || "",
      timer: allTablesData?.timer || "",
      tip: null,
      tipType: "",
      totalAmount: Number(cartData?.basePrice) || 0, // or calculate if needed
      finalPrice: Number(cartData?.finalPrice) || 0,
    };

    const onSuccess = () => {
      toast.success("Order placed successfully");
      dispatch(emptyCartAction()); // Clear the cart

      toggleCart();
      setLoading(false);

      dispatch(
        updateTableData(
          user?.restaurantID,
          customerID,
          tableNumber,
          allTablesData?.timer,
          restaurantsDetail?.name
        )
      );
    };

    const onError = (errorMessage) => {
      toast.error("Error while placing order");
      setLoading(false);
    };

    const orderId = await dispatch(
      addOrderAction(orderPayload, onSuccess, onError)
    );
    return orderId;
  };

  return (
    <>
      <>
        <Row className="d-flex justify-content-start align-items-center mx-0 border-bottom border-lighter custom-padding">
          <Col className="col-1 px-0 d-flex justify-content-start">
            <Button size="sm" color="primary" onClick={toggleCart}>
              <i className="fas fa-arrow-left "></i>
            </Button>
          </Col>
          <Col className="col-5 d-flex justify-content-end px-0">
            <h4 className="fs--25 fw--600 mb-0 normal-heading">Cart</h4>
          </Col>
          {cartItems.length > 0 && (
            <Col className="col-6 d-flex justify-content-end pr-0">
              <BsCartXFill
                className="text-danger cursor-pointer"
                title="Empty Cart"
                size={30}
                onClick={() => {
                  dispatch(emptyCartAction());
                }}
              />
            </Col>
          )}
        </Row>
        <Container fluid className="pt-3">
          <Row className="mx-0">
            <Col className="co-12 px-0">
              {cartItems?.length > 0 ? (
                <Row>
                  <Col className="col-12 bg-site-transparent mt-3">
                    <OrderedItems cartItems={cartItems} cartData={cartData} />

                    <Col className="col-12">
                      <PricingSection
                        currency={cartItems?.[0]?.currency || "$"}
                        finalPrice={cartData.finalPrice}
                        basePrice={cartData.basePrice}
                        appliedCharges={cartData.appliedCharges}
                      />
                    </Col>

                    <Col className="col-12">
                      <CustomerData
                        customerData={customerData}
                        setCustomerData={setCustomerData}
                        fieldErrors={fieldErrors}
                        setFieldErros={setFieldErros}
                        setIsValid={setIsValid}
                        bgColor={bgColor}
                      />
                    </Col>
                    {customerAddError?.length > 0 && (
                      <Col className="col-12 justify-content-start fs--12 text-danger px-0">
                        <div>{customerAddError}</div>
                      </Col>
                    )}

                    <Col className="col-12 d-flex justify-content-center mt-2">
                      <Col xl={5} lg={7} md={10} sm={8} xs={10}>
                        <TableSelection
                          tables={tablesDropdown()}
                          tableNumber={tableNumber}
                          setTableNumber={setTableNumber}
                          bgColor={bgColor}
                        />
                      </Col>
                    </Col>

                    <Col className="col-12 d-flex justify-content-center mt-4">
                      <Col
                        xl={5}
                        lg={7}
                        md={9}
                        sm={7}
                        xs={10}
                        className="px-0 d-flex justify-content-center"
                      >
                        <button
                          className="top_cards__addtocart fs--16 fw--500 w-100 btn text-white p-3"
                          style={{
                            backgroundColor: restaurantsDetail?.adminColor,
                          }}
                          onClick={handlePay}
                          disabled={
                            cartItems?.length === 0 ||
                            loading ||
                            tableNumber === "" ||
                            !isValid
                          }
                        >
                          {loading ? (
                            <Spinner size="sm" color="white" />
                          ) : (
                            `Confirm Order- ${
                              cartItems?.[0]?.currency || "$"
                            } ${cartData.finalPrice}`
                          )}
                        </button>
                      </Col>
                    </Col>
                  </Col>
                </Row>
              ) : (
                <p className="d-flex flex-column justify-content-center align-items-center py-5 mb-0 fw-bold normal-heading">
                  <img
                    src={emptyCartImage}
                    style={{ maxWidth: "80px", maxHeight: "80px" }}
                    className="w-full mr-5 mb-2"
                    alt="empty cart"
                  />
                  Cart is empty. Add an item to proceed
                </p>
              )}
            </Col>
          </Row>
        </Container>
      </>
    </>
  );
};

export default WaiterCart;
